import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';
import {
    getAutoFillRules,
    isAutoFillTriggerMatched,
} from '@/utils/eform-schema-paths';

// Pure path helpers — mirror useEForm.getFieldValue / getNextPayload
// (see src/hooks/use-eform/index.js). Inlined to avoid calling useEForm()
// inside this hook (which would allocate unused internal state).
function getFieldValueAtPath(payload, parentProps) {
    let temp = payload;
    for (const key of parentProps) {
        if (temp === undefined || temp === null) return undefined;
        temp = temp[key];
    }
    return temp;
}

function setFieldValueAtPath(payload, parentProps, value) {
    return produce(payload, (draft) => {
        let temp = draft;
        for (let i = 0; i < parentProps.length; i++) {
            const key = parentProps[i];
            if (i === parentProps.length - 1) {
                temp[key] = value;
            } else {
                if (temp[key] === undefined || temp[key] === null) return;
                temp = temp[key];
            }
        }
    });
}

/**
 * Drives schema-declared `x-autoFillRules`. Watches `payload`; per rule:
 *   - If trigger matches: writes `fillValue` into each `targetFields` entry and
 *     marks the path as auto-filled (so the renderer can lock it read-only).
 *   - If trigger no longer matches AND `clearOnChange: true`: clears each
 *     target path *that we previously auto-filled* (never wipes values the
 *     user typed before any rule fired).
 *
 * Returns `{ autoFilledByRulePaths: Set<string> }`. Pass the Set down through
 * `formHandlers` so `BaseEform` can include it in `effectiveIsReadOnly`.
 *
 * Path format
 *   - `triggerField` / `targetFields` are dot-separated paths matching the
 *     output of `parentPropsToPath(parentProps)`. For top-level fields this is
 *     just the schema key (e.g. `"symptoms_pain_functional_impairment_left_foot"`).
 */
export default function useAutoFillRules(schema, payload, setPayload) {
    const [autoFilledByRulePaths, setAutoFilledByRulePaths] = useState(() => new Set());
    const previouslyFilledRef = useRef(new Set());

    useEffect(() => {
        const rules = getAutoFillRules(schema);
        if (rules.length === 0 || !payload) return;

        const nextAutoFilled = new Set();
        let nextPayload = payload;
        let changed = false;

        rules.forEach((rule) => {
            const { triggerField, triggerValue, targetFields, fillValue, clearOnChange } = rule || {};
            if (typeof triggerField !== 'string' || !Array.isArray(targetFields)) return;

            const triggerPath = triggerField.split('.');
            const triggerCurrent = getFieldValueAtPath(nextPayload, triggerPath);
            const matched = isAutoFillTriggerMatched(triggerCurrent, triggerValue);

            targetFields.forEach((target) => {
                if (typeof target !== 'string' || !target) return;
                const targetPath = target.split('.');

                if (matched) {
                    nextAutoFilled.add(target);
                    if (getFieldValueAtPath(nextPayload, targetPath) !== fillValue) {
                        nextPayload = setFieldValueAtPath(nextPayload, targetPath, fillValue);
                        changed = true;
                    }
                } else if (clearOnChange && previouslyFilledRef.current.has(target)) {
                    if (getFieldValueAtPath(nextPayload, targetPath) !== null) {
                        nextPayload = setFieldValueAtPath(nextPayload, targetPath, null);
                        changed = true;
                    }
                }
            });
        });

        previouslyFilledRef.current = nextAutoFilled;

        setAutoFilledByRulePaths((prev) => {
            if (prev.size === nextAutoFilled.size && [...prev].every((p) => nextAutoFilled.has(p))) {
                return prev;
            }
            return nextAutoFilled;
        });

        if (changed) setPayload(nextPayload);
    }, [payload, schema]);

    return { autoFilledByRulePaths };
}
